import { ExternalLink, Github, Calendar, TrendingUp, Users, Code } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/SEO/SEOHead";
import { CreativeWorkSchema } from "@/components/Schema/CreativeWorkSchema";
import aiEcommerceImg from "@/assets/project-ai-ecommerce.jpg";
import netsuiteImg from "@/assets/project-netsuite.jpg";
import powerbiImg from "@/assets/project-powerbi.jpg";
import celigoImg from "@/assets/project-celigo.jpg";
import aiWorkflowImg from "@/assets/project-ai-workflow.jpg";
import executiveAiImg from "@/assets/project-executive-ai.jpg";
import aiAgentsImg from "@/assets/project-ai-agents.jpg";

const Portfolio = () => {
  const navigate = useNavigate();
  const projects = [
    {
      title: "AI Agents Platform - Multi-Tenant RAG System",
      description: "Enterprise-grade multi-tenant AI platform with complete data isolation, custom knowledge bases, and white-label capabilities. Deployed for multiple organizations with high availability.",
      tech: ["RAG Architecture", "Multi-Tenant", "Vector DB", "LangChain", "Azure OpenAI"],
      image: aiAgentsImg,
      link: "/contact?project=ai-agents",
      github: "#",
      metrics: { improvement: "Significant", timeframe: "2 months", roi: "Strong" },
      status: "Live Now",
      category: "AI Platform",
      client: "Enterprise SaaS Company",
      year: "2024",
      isDemo: false
    },
    {
      title: "Executive AI Dashboard - Unified Business Intelligence",
      description: "Revolutionary AI-powered executive dashboard replacing PowerBI and Tableau with natural language queries, real-time KPI monitoring, and predictive analytics from all business systems.",
      tech: ["Natural Language", "Real-time Analytics", "Predictive AI", "Multi-Source Integration", "Executive Reporting"],
      image: executiveAiImg,
      link: "/contact?project=executive-ai",
      github: "#",
      metrics: { improvement: "Significant", timeframe: "3 months", roi: "Strong" },
      status: "Live Now",
      category: "Business Intelligence",
      client: "Fortune 500 Manufacturing",
      year: "2024",
      isDemo: false
    },
    {
      title: "AI Workflow Engine - Intelligent Process Automation",
      description: "Visual workflow designer with AI decision nodes connecting 100+ systems. Automates complex business processes using natural language triggers and intelligent routing.",
      tech: ["Visual Designer", "AI Decision Nodes", "100+ Connectors", "Natural Language", "Process Mining"],
      image: aiWorkflowImg,
      link: "/contact?project=ai-workflow",
      github: "#",
      metrics: { improvement: "Significant", timeframe: "4 months", roi: "Strong" },
      status: "Beta Access",
      category: "Automation",
      client: "Global Healthcare Network",
      year: "2024",
      isDemo: false
    },
    {
      title: "AI-Powered E-commerce Platform",
      description: "Revolutionary Shopify app with machine learning recommendations, automated inventory management, and predictive analytics delivering 40% increase in sales for enterprise clients.",
      tech: ["Shopify Plus", "TensorFlow", "React", "Node.js", "MongoDB"],
      image: aiEcommerceImg,
      link: "/contact?project=ai-ecommerce", 
      github: "#",
      metrics: { improvement: "40%", timeframe: "3 months", roi: "Positive" },
      status: "Live Now",
      category: "E-commerce",
      client: "Fashion Retail Chain",
      year: "2023",
      isDemo: false
    },
    {
      title: "NetSuite AI Scripts - Intelligent ERP Automation",
      description: "AI-powered NetSuite scripts with intelligent workflow automation, natural language report builder, and conversational AI for complex ERP operations.",
      tech: ["SuiteScript AI", "Workflow Automation", "Natural Language", "Smart Validation", "ERP Intelligence"],
      image: netsuiteImg,
      link: "/contact?project=netsuite-ai",
      github: "#",
      metrics: { improvement: "65%", timeframe: "6 months", roi: "Positive" },
      status: "Live Now",
      category: "ERP", 
      client: "Technology Services Corp",
      year: "2023",
      isDemo: false
    },
    {
      title: "Real-time Analytics Dashboard",
      description: "Advanced PowerBI solution with live data streaming, predictive analytics, and AI-powered insights processing 1M+ data points daily with natural language queries.",
      tech: ["PowerBI", "Azure Synapse", "Python", "Apache Kafka", "Redis"],
      image: powerbiImg,
      link: "/contact?project=powerbi-analytics",
      github: "#",
      metrics: { improvement: "85%", timeframe: "4 months", roi: "Positive" },
      status: "Live Now",
      category: "Analytics",
      client: "Financial Services Firm", 
      year: "2023",
      isDemo: false
    }
  ];

  const categories = ["All", "AI Platform", "Business Intelligence", "Automation", "E-commerce", "ERP", "Analytics"];
  
  return (
    <>
      <SEOHead
        title="Our Portfolio | Enterprise AI & Digital Transformation Case Studies"
        description="Explore our portfolio of successful enterprise AI implementations, digital transformation projects, and custom software solutions delivering measurable ROI."
        canonicalUrl="https://www.fortifytek.com/portfolio"
        keywords={["case studies", "portfolio", "success stories", "enterprise AI projects", "digital transformation examples"]}
      />
      <CreativeWorkSchema
        headline="Enterprise AI & Digital Transformation Portfolio"
        description="A collection of case studies demonstrating successful implementation of AI and digital transformation solutions."
        image="https://www.fortifytek.com/og-image.png"
        datePublished="2024-01-01"
        author="Fortify"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        {/* Hero Section */}
      <section className="pt-40 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-8 leading-tight animate-fade-in">
              <span className="block mb-2">
                Client
              </span>
              <span className="block text-amber-400 pb-2">
                Success Stories
              </span>
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              Featured projects showcasing the impact we create. Your project could be next.
            </p>
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-24 relative overflow-hidden bg-background">
        <div className="absolute inset-0"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className="group bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-colors"
              >
                <div className="aspect-video bg-zinc-900 relative overflow-hidden flex items-center justify-center">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  
                  {/* Status Badge */}
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      project.status === 'Live Now' 
                        ? 'bg-green-600/90 text-white' 
                        : 'bg-orange-600/90 text-white'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                  
                  {/* Category Badge */}
                  <div className="absolute top-4 right-20">
                    <span className="px-3 py-1 bg-blue-600/90 text-white rounded-full text-xs font-medium">
                      {project.category}
                    </span>
                  </div>
                  
                  {/* Performance Metrics Overlay */}
                  <div className="absolute bottom-4 left-4 bg-zinc-950/90 backdrop-blur-xl rounded-lg px-3 py-2 border border-white/10">
                    <div className="text-xs text-white/70">ROI</div>
                    <div className="text-lg font-bold text-green-400">{project.metrics.roi}</div>
                  </div>
                  
                  <div className="absolute top-4 right-4 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/20 backdrop-blur-xl"
                    onClick={() => {
                      if (project.isDemo === false) {
                        window.location.href = project.link;
                      } else {
                        window.open(project.link, '_blank');
                      }
                    }}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/20 backdrop-blur-xl"
                    onClick={() => {
                      if (project.github === '#') {
                        alert('This is a confidential client project. Contact us to learn more about our capabilities.');
                      } else {
                        window.open(project.github, '_blank');
                      }
                    }}
                  >
                    <Github className="w-4 h-4" />
                  </Button>
                  </div>
                </div>
                
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-white/60">{project.client}</div>
                    <div className="text-sm text-blue-300">{project.year}</div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-4 group-hover:text-amber-400 transition-colors">
                    {project.title}
                  </h3>
                  
                  <p className="text-white/70 mb-6 leading-relaxed line-clamp-3">
                    {project.description}
                  </p>
                  
                  {/* Project Metrics */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <div className="text-xs text-green-200/60">Performance</div>
                      </div>
                      <div className="text-lg font-bold text-green-400">{project.metrics.improvement}</div>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-blue-400" />
                        <div className="text-xs text-blue-200/60">Timeline</div>
                      </div>
                      <div className="text-lg font-bold text-blue-400">{project.metrics.timeframe}</div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tech.slice(0, 3).map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-3 py-1 bg-indigo-500/10 text-indigo-300 rounded-full text-xs font-medium border border-indigo-500/20"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.tech.length > 3 && (
                      <span className="px-3 py-1 bg-white/10 text-white/60 rounded-full text-xs font-medium">
                        +{project.tech.length - 3} more
                      </span>
                    )}
                  </div>
                  
                  <Button 
                    className="w-full bg-white text-black hover:bg-zinc-200 font-medium transition-colors group"
                    onClick={() => {
                      if (project.isDemo === false) {
                        window.location.href = project.link;
                      } else {
                        window.open(project.link, '_blank');
                      }
                    }}
                  >
                    {project.isDemo === false ? 'Learn More' : 'View Project'}
                    <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Button 
              size="lg"
              className="bg-white text-black hover:bg-zinc-200 px-8 py-4 rounded-full text-lg font-medium transition-colors"
              onClick={() => navigate('/contact')}
            >
              Start Your Project Today
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
    </>
  );
};

export default Portfolio;